import { EMPTY_DISPLAY_VALUE } from '../../../constants/formatting';
import { Nullable } from '../../../types/common.types';
import isFiniteNumber from '../isFiniteNumber';

import formatNumber, { FormatNumberOptions } from './formatNumber';

type PercentageScaleFn = (value: number) => number;

const defaultScaleFn: PercentageScaleFn = value => value * 100;

export type FormatPercentageOptions = FormatNumberOptions & {
  /**
   * By default value multiplied by 100,
   * if you want custom behavior you can either pass custom function,
   * or pass `false` in case you want to skip scale fn completely
   */
  scale?: PercentageScaleFn | false;
};

/**
 * Formats a number as a percentage string with two decimal places.
 *
 * @param value - The number to format as a percentage. It's expected that this is in 0-1 scale where 1 is 100% (for custom behavior please use `scale` option)
 * @returns The formatted percentage string or `EMPTY_DISPLAY_VALUE` if the value is nullish (for custom fallback value please use `fallback` option)
 *
 * @example
 * formatPercentage(0.1234);
 * // Returns '12.34%'
 *
 * formatPercentage(0.1234, { scale: false });
 * // Returns `0.1234%`
 */
const formatPercentage = (
  value: Nullable<number>,
  options?: FormatPercentageOptions,
): string => {
  const {
    fallback = EMPTY_DISPLAY_VALUE,
    scale = defaultScaleFn,
    ...formatNumberOptions
  } = options || {};

  if (!isFiniteNumber(value)) return fallback;

  const scaledValue = scale === false ? value : scale(value);

  return `${formatNumber(scaledValue, {
    ...formatNumberOptions,
    fallback,
  })}%`;
};

export default formatPercentage;
